import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Button, MoneyInput, PercentInput } from '@refera/ui-web'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Slide,
  Typography,
} from '@material-ui/core'

import useStyles from './styles'
import { referaItems, secondaryItems, saasItems } from './constants'
import { displayCurrency, displayPercentage } from '../utils'
import Draggable from 'react-draggable'
import IconButton from '_/components/svg/icon-button'
import CloseIcon from '@material-ui/icons/Close'
import classNames from 'classnames'
import { round } from '_/views/finance/utils/functions'
import { useDialog } from '_/hooks/use-dialog'
import useRolePermission from '_/hooks/use-role-permission'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'
import { CanDoButtonAction } from '_/utils/can-do-button-action'
import { BUTTONS_ACTION } from '_/utils/constants/service-order'
import { useToast } from '_/hooks/use-toast'
import { BaseSwitch } from '_/components/inputs'
import { AdditionalWarrantyMonthsModal } from '../additional-warranty-months'

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
})

const PaperComponent = props => {
  return (
    <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} />
    </Draggable>
  )
}

export function ReferaTaxesModal({
  budget,
  handleInputChange,
  open,
  close,
  onSaveBudget,
  isFromSAASAgencyAndNotReferaService,
  useExtendedWarranty,
}) {
  const styles = useStyles()
  const [inputValue, setInputValue] = useState()
  const [inputPercentage, setInputPercentage] = useState()
  const [initialBudget] = useState(budget)
  const [extendedWarranty, setExtendedWarranty] = useState(budget?.extendedWarranty ?? false)
  const [extendedWarrantyModalOpen, setExtendedWarrantyModalOpen] = useState(false)

  const { checkUserPermission } = useRolePermission()

  const { showDialog, closeDialog } = useDialog()
  const { showToast } = useToast()

  const canEditBudget = CanDoButtonAction({
    nameButton: BUTTONS_ACTION.EDIT_BUDGET,
  })

  const extendedWarrantyValue = useMemo(() => {
    const currentValue =
      inputValue?.item?.name === 'extendedWarrantyValue'
        ? inputValue?.floatValue
        : budget?.extendedWarrantyValue

    return extendedWarranty ? Number(currentValue) || 0 : 0
  }, [budget?.extendedWarrantyValue, extendedWarranty, inputValue])

  const referaCompletionValue = useMemo(() => {
    return (
      budget?.referaRegularTax +
      budget?.additionalTakeRate +
      budget?.warrantyValue +
      budget?.revenueShare +
      extendedWarrantyValue
    )
  }, [budget, extendedWarrantyValue])

  const referaCompletionPercentage = useMemo(() => {
    return round((referaCompletionValue / budget?.totalPriceTradesman) * 100, 8)
  }, [budget, referaCompletionValue])

  const totalPrice = useMemo(() => {
    return (
      budget?.totalPriceTradesman +
      budget?.agencyCompletionPercentageValue +
      referaCompletionValue +
      budget?.financialIncomeValue
    )
  }, [
    referaCompletionValue,
    budget?.agencyCompletionPercentageValue,
    budget?.totalPriceTradesman,
    budget?.financialIncomeValue,
  ])

  const grossTotalPrice = useMemo(() => {
    return totalPrice
  }, [totalPrice])

  const finalValues = useMemo(() => {
    const withholdingTaxValue = round(totalPrice * (budget?.withholdingTax / 100.0), 2) || 0

    return {
      totalPrice,
      withholdingTaxValue,
      totalPriceServiceProvider: round(budget?.totalPriceTradesman - withholdingTaxValue, 2) || 0,
      liquidValue: round(totalPrice - withholdingTaxValue, 2) || 0,
    }
  }, [budget?.totalPriceTradesman, budget?.withholdingTax, totalPrice])

  const handleChangeValues = useCallback(
    (item, type) => {
      let percentageValue = 0
      let input = 0
      let percentageEvent
      let valueEvent

      if (type === 'value') {
        if (inputValue?.floatValue) {
          percentageValue = (inputValue?.floatValue / budget.totalPriceTradesman) * 100 || 0
          input = inputValue?.floatValue
        }

        if (
          inputValue?.item?.name === item.name &&
          input !== Number(parseFloat(budget[item.name])?.toFixed(2))
        ) {
          if (item.complementName) {
            percentageEvent = {
              target: { name: item.complementName, value: round(percentageValue, 4) },
            }
          }
          valueEvent = {
            target: { name: item.name, value: round(input, 2) || 0 },
          }
        }
      }
      if (type === 'percentage') {
        if (inputPercentage?.floatValue) {
          percentageValue = (inputPercentage?.floatValue / 100.0) * budget.totalPriceTradesman || 0
          input = inputPercentage?.floatValue
        }
        if (
          inputPercentage?.item?.name === item.name &&
          input !== Number(parseFloat(budget[item.name])?.toFixed(2))
        ) {
          if (item.complementName) {
            percentageEvent = {
              target: { name: item.complementName, value: round(percentageValue, 2) },
            }
          }
          valueEvent = {
            target: {
              name: item.name,
              value: Number(round(input, 4)) || 0,
            },
          }
        }
      }

      if (percentageEvent) {
        handleInputChange(percentageEvent)
      }

      if (valueEvent) {
        handleInputChange(valueEvent)
      }
    },
    [handleInputChange, budget, inputValue, inputPercentage]
  )

  const renderTotalPriceTradesman = useMemo(() => {
    return (
      <Grid className={classNames(styles.headerWrapper, styles.black, styles.bold)}>
        <span className={classNames(styles.label, styles.black)} style={{ fontSize: '16px' }}>
          Valor prestador:
        </span>
        <span className={classNames(styles.value, styles.black)}>
          {displayCurrency(budget.totalPriceTradesman)}
        </span>
      </Grid>
    )
  }, [budget.totalPriceTradesman])

  const handleExtendedWarranty = useCallback(
    e => {
      const { checked } = e.target
      setExtendedWarranty(checked)

      handleInputChange({
        target: {
          name: 'extendedWarranty',
          value: checked,
        },
      })

      if (!checked) {
        ;[
          {
            name: 'extendedWarrantyValue',
            value: 0,
          },
          {
            name: 'additionalWarrantyMonths',
            value: null,
          },
        ].forEach(field => {
          handleInputChange({
            target: field,
          })
        })
      }
    },
    [handleInputChange]
  )

  const renderItems = useCallback(
    itemsToRender => {
      const hasExtendedWarranty = extendedWarranty || useExtendedWarranty
      return itemsToRender({ budget, styles, hasExtendedWarranty })?.map(item => {
        const columns = item.fields.length
        return (
          <Grid key={item.label} className={styles.item}>
            <Typography
              className={classNames(styles.label, item?.boldLabel && [styles.bold, styles.black])}
            >
              {item?.label}
            </Typography>
            {columns === 1 && <div />} {/* To push solo items to the end of the grid */}
            {item.fields.map(field => {
              const { value, type } = field
              const isDisabled = field.name === 'extendedWarrantyValue' && !extendedWarranty
              if (field.name === 'extendedWarranty') {
                return (
                  <BaseSwitch
                    key={field.name}
                    checked={value ?? false}
                    name={field.name}
                    onChange={handleExtendedWarranty}
                  />
                )
              }

              if (type === 'percentage-input') {
                return (
                  <PercentInput
                    key={field.name}
                    value={displayPercentage(value ?? 0)}
                    mask=" "
                    format="####"
                    placeholder="%"
                    allowNegative={false}
                    allowLeadingZeros={false}
                    className={styles.input}
                    onBlur={() => {
                      handleChangeValues(field, 'percentage')
                      if (field.name === 'additionalTakeRatePerc') {
                        handleInputChange({
                          target: { name: 'additionalTakeRateChange', value: true },
                        })
                      }
                    }}
                    onChange={({ floatValue }) =>
                      setInputPercentage({ floatValue: floatValue ?? 0, item: field })
                    }
                    style={{ fontSize: '16px', textAlign: 'right' }}
                  />
                )
              }
              if (type === 'currency-input') {
                return (
                  <MoneyInput
                    key={field.name}
                    value={displayPercentage(value ?? 0)}
                    style={{ fontSize: '16px' }}
                    placeholder="R$ "
                    allowNegative={false}
                    allowLeadingZeros={false}
                    className={styles.input}
                    disabled={isDisabled}
                    onBlur={() => {
                      handleChangeValues(field, 'value')
                      if (field.name === 'additionalTakeRate') {
                        handleInputChange({
                          target: { name: 'additionalTakeRateChange', value: true },
                        })
                      }
                    }}
                    onChange={({ floatValue }) =>
                      setInputValue({ floatValue: floatValue ?? 0, item: field })
                    }
                  />
                )
              }
              if (type === 'currency-read') {
                return (
                  <Typography key={field.name} className={styles.readField}>
                    {displayCurrency(value)}
                  </Typography>
                )
              }
              return null
            })}
          </Grid>
        )
      })
    },
    [
      budget,
      styles,
      setInputPercentage,
      setInputValue,
      handleChangeValues,
      extendedWarranty,
      useExtendedWarranty,
      handleExtendedWarranty,
    ]
  )

  const renderReferaCompletionValues = useMemo(() => {
    return (
      <Grid className={styles.item} style={{ padding: '0 24px', marginTop: '12px' }}>
        <span className={classNames(styles.label, styles.black, styles.bold)}>
          Tarifa da refera:
        </span>
        <span className={classNames(styles.value, styles.black)}>
          {displayPercentage(referaCompletionPercentage)}
        </span>
        <span className={classNames(styles.value, styles.black)}>
          {displayCurrency(referaCompletionValue)}
        </span>
      </Grid>
    )
  }, [referaCompletionPercentage, referaCompletionValue])

  const renderSoloItem = useMemo(
    () => (name, value, style) => {
      return (
        <Grid className={styles.soloItem} style={style}>
          <span className={classNames(styles.label, styles.black, styles.bold)}>{name}</span>
          <span className={classNames(styles.value, styles.black, styles.soloItemValue)}>
            {value}
          </span>
        </Grid>
      )
    },
    []
  )

  const renderFinalValue = useMemo(() => {
    return (
      <Grid className={styles.finalItem}>
        <span className={classNames(styles.label, styles.black)} style={{ fontSize: '20px' }}>
          Valor bruto: {displayCurrency(grossTotalPrice)}
        </span>
      </Grid>
    )
  }, [grossTotalPrice])

  const handleUpdateFinalValues = useCallback(() => {
    const itemsToUpdate = [
      { name: 'referaCompletionPercentage', value: referaCompletionPercentage },
      { name: 'referaCompletionPercentageValue', value: referaCompletionValue },
      { name: 'totalPrice', value: finalValues.totalPrice },
      { name: 'withholdingTaxValue', value: finalValues.withholdingTaxValue },
      { name: 'totalPriceServiceProvider', value: finalValues.totalPriceServiceProvider },
      { name: 'liquidValue', value: finalValues.liquidValue },
    ]

    itemsToUpdate.forEach(item => {
      handleInputChange({ target: { name: item.name, value: item.value } })
    })
  }, [referaCompletionPercentage, referaCompletionValue, finalValues, handleInputChange])

  const handleSave = useCallback(() => {
    if (!canEditBudget) {
      showToast({
        type: 'error',
        message: 'Este orçamento está em uma etapa que não permite edição.',
      })
      return
    }
    const hasValidAdditionalWarrantyMonths =
      budget.additionalWarrantyMonths !== '' &&
      budget.additionalWarrantyMonths !== undefined &&
      budget.additionalWarrantyMonths !== null

    if (extendedWarranty && !hasValidAdditionalWarrantyMonths) {
      setExtendedWarrantyModalOpen(true)
      return
    }

    onSaveBudget({
      extendedWarranty,
      extendedWarrantyValue,
      referaCompletionPercentage,
      referaCompletionPercentageValue: referaCompletionValue,
      ...finalValues,
    })
    close()
  }, [
    close,
    onSaveBudget,
    canEditBudget,
    showToast,
    extendedWarranty,
    budget,
    extendedWarrantyValue,
    referaCompletionPercentage,
    referaCompletionValue,
    finalValues,
  ])

  const handleResetFieldsAndCloseModal = useCallback(() => {
    // Restores budget to its initial state
    Object.keys(initialBudget).forEach(key => {
      if (initialBudget[key] !== budget[key]) {
        handleInputChange({ target: { name: key, value: initialBudget[key] } })
      }
    })
    closeDialog()
    close()
  }, [initialBudget, budget, handleInputChange, close, closeDialog])

  const handleCancel = useCallback(() => {
    showDialog({
      type: 'warning',
      subject: 'Você tem certeza que deseja fechar a janela sem salvar o registro?',
      onApprove: handleResetFieldsAndCloseModal,
      onCancel: closeDialog,
      labelCancel: 'Não',
      labelApprove: 'Sim',
    })
  }, [handleResetFieldsAndCloseModal, showDialog, closeDialog])

  const renderDialogActions = useMemo(() => {
    return (
      <DialogActions className={styles.dialogActions}>
        <Button onClick={handleCancel} variant="secondary" color="red">
          Cancelar
        </Button>
        <Button
          onClick={() =>
            checkUserPermission({
              permission: PERMISSIONS.BUDGETS,
              permissionAction: [PERMISSIONS_ACTIONS.ADD, PERMISSIONS_ACTIONS.EDIT],
              functionAccess: handleSave,
            })
          }
          color="primary"
        >
          Salvar
        </Button>
      </DialogActions>
    )
  }, [close, handleCancel, handleSave])

  useEffect(() => {
    handleUpdateFinalValues()
  }, [handleUpdateFinalValues])

  return (
    <>
      <Dialog
        open={open}
        onClose={close}
        className={styles.dialog}
        PaperComponent={PaperComponent}
        TransitionComponent={Transition}
        aria-describedby="Modal de tarifas da refera"
        disableEnforceFocus
        disableAutoFocus
        disableRestoreFocus
      >
        <IconButton onClick={handleCancel} className={styles.closeButton}>
          <CloseIcon />
        </IconButton>
        <DialogTitle id="draggable-dialog-title" className={styles.title}>
          {isFromSAASAgencyAndNotReferaService ? 'Tarifas da intermediária' : 'Tarifas da refera'}
        </DialogTitle>
        <Divider />
        <DialogContent className={styles.content}>
          {renderTotalPriceTradesman}
          <Divider />
          {isFromSAASAgencyAndNotReferaService ? (
            <>
              <Grid className={styles.itemsContainer}>{renderItems(saasItems)}</Grid>
              <Divider />
              <Grid className={styles.finalPricesWrapper} style={{ paddingBottom: '12px' }}>
                {renderSoloItem(
                  'Receita financeira:',
                  displayCurrency(budget?.financialIncomeValue)
                )}
              </Grid>
              <Grid className={styles.finalPricesWrapper}>{renderFinalValue}</Grid>
            </>
          ) : (
            <>
              <Grid className={styles.itemsContainer}>{renderItems(referaItems)}</Grid>
              <Divider />
              {renderReferaCompletionValues}
              <Grid className={styles.finalPricesWrapper} style={{ paddingBottom: '12px' }}>
                {renderItems(secondaryItems)}
                {renderSoloItem(
                  'Receita financeira:',
                  displayCurrency(budget?.financialIncomeValue)
                )}
              </Grid>
              <Grid className={styles.finalPricesWrapper}>{renderFinalValue}</Grid>
            </>
          )}
        </DialogContent>
        {renderDialogActions}
      </Dialog>
      <AdditionalWarrantyMonthsModal
        budget={budget}
        openModal={extendedWarrantyModalOpen}
        setOpenModal={setExtendedWarrantyModalOpen}
        handleInputChange={handleInputChange}
        onSaveBudget={onSaveBudget}
        closeMainModal={close}
      />
    </>
  )
}
